import React, { useContext, useEffect, useState } from 'react'
import { getImage, getVideoThumbnail, raiseToast } from '../../utils/helper'
import { AppContext } from '../../context/app'
import useAxios from 'axios-hooks'
import { ANALYTICS_API, PLAYER_SESSION } from '../../utils/apiUrls'
import { LG_SIZE, SM_SIZE } from '../../utils/constants'
import ChannelPlayerVideoJs from '../../components/player/channelPlayerVideoJs'
import { ContentContext } from '../../context/content'
import { isEmpty } from 'lodash'
import ExternalLinkPlayer from '../playerTemplate/externalLinkPlayer'
import IptvPlayerVideoJs from '../../components/player/iptvPlayerVideoJs'
import CustomImage from '../../components/customImage/customImage'

let FIRST_PROGRAM_PLAYED = false

const ChannelPlayer = (props) => {
  let PLAYER_STARTED = false
  const {
    contentInfo,
    contentWatchUrl,
    resumeTime,
    handlePlayingSequence = () => {},
    watermarkLogo,
    detailObj,
    setMetadata = () => {},
    isIptvPlayer = false,
    pageData = {},
  } = props
  const [error, setError] = useState(false)

  const isLoopedProgram = contentInfo?.playUrl ? contentInfo?.is_looped : true

  const playerRef = React.useRef(null)
  const { accountObj, isLogin } = useContext(AppContext)
  const { updateContentResumeTimestamp } = useContext(ContentContext)
  const [source, setSource] = useState('WEB')
  const [volume, setVolume] = useState(100)
  const [isBuffering, setIsBuffering] = useState(false)
  const randomVal = Math.floor(1000 + Math.random() * 9000)

  // Parse m3u8 URL and extract metadata
  const parseM3u8Metadata = async (url) => {
    try {
      // First fetch - get master playlist or initial manifest
      let response = await fetch(url)
      let manifestText = await response.text()

      // Check if this is a master playlist containing variant URLs
      let variantUrl = null
      const lines = manifestText.split('\n')

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()

        // Look for variant playlist URLs after EXT-X-STREAM-INF
        if (line.startsWith('#EXT-X-STREAM-INF')) {
          // Next non-empty line should be the URL
          for (let j = i + 1; j < lines.length; j++) {
            const nextLine = lines[j].trim()
            if (nextLine && !nextLine.startsWith('#')) {
              // Resolve relative URLs
              if (nextLine.startsWith('http')) {
                variantUrl = nextLine
              } else {
                // Relative URL - resolve against base URL
                const baseUrl = url.substring(0, url.lastIndexOf('/') + 1)
                variantUrl = baseUrl + nextLine
              }
              break
            }
          }
          if (variantUrl) break
        }
      }

      // If we found a variant URL, fetch that instead
      if (variantUrl) {
        console.log('Found variant playlist URL, fetching:', variantUrl)
        response = await fetch(variantUrl)
        manifestText = await response.text()
      }

      let title = null
      let artist = null

      // Parse the final manifest text line by line
      const finalLines = manifestText.split('\n')

      for (let i = 0; i < finalLines.length; i++) {
        const line = finalLines[i].trim()

        // Skip empty lines
        if (!line) continue

        // Extract title from custom tag format: title="value"
        if (line.includes('title=')) {
          const match = line.match(/title="([^"]+)"/)
          if (match) {
            title = match[1]
          }
        }

        // Extract artist from custom tag format: artist="value"
        if (line.includes('artist=')) {
          const match = line.match(/artist="([^"]+)"/)
          if (match) {
            artist = match[1]
          }
        }

        // Alternative: Extract from EXTINF line (format: #EXTINF:duration,title)
        if (line.startsWith('#EXTINF:') && !title) {
          const parts = line.split(',')
          if (parts.length > 1) {
            const extinf = parts[1].trim()
            if (extinf && extinf !== '') {
              title = extinf
            }
          }
        }

        // Extract from EXT-X-TITLE tag
        if (line.startsWith('#EXT-X-TITLE:') && !title) {
          title = line.substring('#EXT-X-TITLE:'.length).trim()
        }

        // Extract from EXT-X-ARTIST tag
        if (line.startsWith('#EXT-X-ARTIST:') && !artist) {
          artist = line.substring('#EXT-X-ARTIST:'.length).trim()
        }
      }

      setMetadata({ title, artist })

      return { title, artist }
    } catch (err) {
      console.error('Error parsing m3u8:', err)
      setMetadata({ title: null, artist: null })
      return { title: null, artist: null }
    }
  }

  useEffect(() => {
    if (!contentWatchUrl) return
    if (detailObj?.feed_type === 'AUDIO' && detailObj?.is_rcs) {
      parseM3u8Metadata(contentWatchUrl)
    }
  }, [contentWatchUrl])

  const isYoutubePlayer = !isEmpty(contentInfo?.youtube_data)
  const isAudioFeed = detailObj?.feed_type === 'AUDIO'

  const sessionLimit = accountObj?.od_resource_allowances?.session_limit

  const trackingId = `${contentInfo?.content_id + Date.now() + randomVal}`

  const handleResize = () => {
    if (window.innerWidth > LG_SIZE) {
      setSource('WEB')
    } else if (window.innerWidth < LG_SIZE && window.innerWidth > SM_SIZE) {
      setSource('TABLET')
    } else {
      setSource('MOBILE')
    }
  }

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const [{ error: analyticsError }, analyticsEventCall] = useAxios(
    {
      url: ANALYTICS_API,
      method: 'post',
    },
    { manual: true, autoCancel: false },
  )

  const [{ error: sessionError }, sessionEventCall] = useAxios(
    {
      url: PLAYER_SESSION,
      method: 'post',
    },
    { manual: true, autoCancel: false },
  )

  useEffect(() => {
    console.error(sessionError)
  }, [sessionError])

  useEffect(() => {
    console.error(analyticsError)
  }, [analyticsError])

  useEffect(() => {
    if (error) {
      handleError()
    }
  }, [error])

  const getSourceType = () => {
    if (contentInfo?.file_metadata?.file_type.includes('audio')) {
      return `audio/mp3`
    }

    return 'application/x-mpegURL'
  }

  const videoJsOptions = {
    // lookup the options in the docs for more options
    autoplay: true,
    muted: !FIRST_PROGRAM_PLAYED,
    responsive: true,
    liveui: true,
    poster: isAudioFeed
      ? getImage(accountObj?.cf_domain_name, detailObj?.channel_thumbnail)
      : getImage(accountObj?.cf_domain_name, contentInfo?.thumbnail),
    // fluid: true,
    loop: isLoopedProgram,
    userActions: {
      hotkeys: true,
    },
    loadingSpinner: false,
    fill: true,
    sources: [
      {
        src: contentWatchUrl,
        type: getSourceType(),
      },
    ],
    plugins: {
      qualityLevels: {},
      hlsQualitySelector: {},
    },
    html5: {
      hls: {
        overrideNative: true,
      },
    },
  }

  const handleError = () => {
    if (!playerRef.current) {
      return
    }
    // playerRef.current.suppressNotSupportedError(true);
  }

  useEffect(() => {
    if (playerRef.current) {
      playVideo(contentWatchUrl)
    }
  }, [contentWatchUrl])

  const playVideo = (url) => {
    let videoElm = playerRef.current

    if (!videoElm.paused) {
      videoElm.pause()
    }
    videoElm.src({ type: 'application/x-mpegURL', src: url })

    videoElm.load()
    videoElm.play()
  }

  const handlePlayerReady = (player) => {
    playerRef.current = player
    let timeInterval
    let previousTime = 0
    let currentTime = 0
    let position = 0

    const isPlayerActive = () => {
      return !!player && !(player?.isDisposed && player.isDisposed())
    }

    const getSafeCurrentTime = () => {
      if (!isPlayerActive()) return null
      try {
        return player.currentTime()
      } catch (err) {
        return null
      }
    }

    const getSafeDuration = () => {
      if (!isPlayerActive()) return 0
      try {
        return player.duration()
      } catch (err) {
        return 0
      }
    }

    const getSafeVideoHeight = () => {
      if (!isPlayerActive()) return 0
      try {
        return player.videoHeight()
      } catch (err) {
        return 0
      }
    }

    // you can handle player events here
    player.on('waiting', () => {
      console.log('player is Buffering')
      setIsBuffering(true)
    })

    player.on('playing', () => {
      setIsBuffering(false)
    })
    player.on('canplaythrough', () => setIsBuffering(false))

    player.on('ready', () => {
      player.playsinline(true)
    })

    player.on('loadedmetadata', async () => {
      if (resumeTime > 0) {
        if (player.duration() < resumeTime) {
          let tempResumeTime = resumeTime
          let playerDuration = player.duration()

          while (playerDuration < tempResumeTime) {
            tempResumeTime -= playerDuration
          }
          player?.currentTime(tempResumeTime)
        } else {
          player?.currentTime(resumeTime)
        }
      }

      await analyticsEventCall({
        data: {
          event_type: 'PLAYER',
          event_name: 'PLAYER_START',
          content_id: contentInfo?.content_id,
          player_timestamp: `0`,
          total_duration: `${player.duration()}`,
          tracking_id: trackingId,
          source: source,
          variant: `${player.videoHeight()}`,
        },
      })

      let playerStartedRes = await analyticsEventCall({
        data: {
          event_type: 'PLAYER',
          event_name: 'PLAYER_PLAY',
          content_id: contentInfo?.content_id,
          player_timestamp: `0`,
          total_duration: `${player.duration()}`,
          tracking_id: trackingId,
          source: source,
          variant: `${player.videoHeight()}`,
        },
      })
      FIRST_PROGRAM_PLAYED = true
      playerStartedRes?.data?.success ? (PLAYER_STARTED = true) : (PLAYER_STARTED = false)

      timeInterval = setInterval(async () => {
        if (PLAYER_STARTED) {
          const safeCurrentTime = getSafeCurrentTime()
          if (safeCurrentTime === null) {
            clearInterval(timeInterval)
            return
          }

          updateContentResumeTimestamp(contentInfo?.content_id, safeCurrentTime)
          if (detailObj?.feed_type === 'AUDIO' && detailObj?.is_rcs) {
            parseM3u8Metadata(contentWatchUrl)
          }
          await analyticsEventCall({
            data: {
              event_type: 'PLAYER',
              event_name: 'PLAYER_PING',
              content_id: contentInfo?.content_id,
              player_timestamp: `${safeCurrentTime}`,
              total_duration: `${getSafeDuration()}`,
              tracking_id: trackingId,
              source: source,
              variant: `${getSafeVideoHeight()}`,
            },
          })
          if (isLogin && sessionLimit) {
            await sessionEventCall({
              data: {
                event_type: 'UPDATE',
                content_id: contentInfo?.content_id,
                session_limit: sessionLimit,
              },
            })
          }
        }
      }, 30000)
    })

    player.on('play', async () => {
      if (PLAYER_STARTED && !player.seeking()) {
        await analyticsEventCall({
          data: {
            event_type: 'PLAYER',
            event_name: 'PLAYER_PLAY',
            content_id: contentInfo?.content_id,
            player_timestamp: `${player?.currentTime()}`,
            total_duration: `${player.duration()}`,
            tracking_id: trackingId,
            source: source,
            variant: `${player.videoHeight()}`,
          },
        })
      }
    })

    player.on('pause', async () => {
      if (PLAYER_STARTED && !player.seeking() && player?.currentTime() < player.duration()) {
        await analyticsEventCall({
          data: {
            event_type: 'PLAYER',
            event_name: 'PLAYER_PAUSE',
            content_id: contentInfo?.content_id,
            player_timestamp: `${player?.currentTime()}`,
            total_duration: `${player.duration()}`,
            tracking_id: trackingId,
            source: source,
            variant: `${player.videoHeight()}`,
          },
        })
      }
      // clearInterval(timeInterval)
      // console.log('player is paused', player.textTracks())
    })

    player.on('timeupdate', function () {
      previousTime = currentTime
      try {
        currentTime = Math.floor(player?.currentTime())
      } catch (err) {
        return
      }

      // save 'position' so long as time is moving forward with each update
      if (previousTime < currentTime) {
        position = previousTime
        previousTime = currentTime
      }
    })

    player.on('volumechange', () => {
      setVolume(player.volume() * 100)
      localStorage.setItem('volume', player.volume())
    })

    player.on('seeked', async () => {
      if (PLAYER_STARTED) {
        await analyticsEventCall({
          data: {
            event_type: 'PLAYER',
            event_name: 'PLAYER_PAUSE',
            content_id: contentInfo?.content_id,
            player_timestamp: `${position}`,
            total_duration: `${player.duration()}`,
            tracking_id: trackingId,
            source: source,
            variant: `${player.videoHeight()}`,
          },
        })

        if (!player.paused()) {
          await analyticsEventCall({
            data: {
              event_type: 'PLAYER',
              event_name: 'PLAYER_PLAY',
              content_id: contentInfo?.content_id,
              player_timestamp: `${player?.currentTime()}`,
              total_duration: `${player.duration()}`,
              tracking_id: trackingId,
              source: source,
              variant: `${player.videoHeight()}`,
            },
          })
        }
      }
    })

    player.on('dispose', async () => {
      if (PLAYER_STARTED) {
        const safeCurrentTime = getSafeCurrentTime()
        await analyticsEventCall({
          data: {
            event_type: 'PLAYER',
            event_name: 'PLAYER_CLOSE',
            content_id: contentInfo?.content_id,
            player_timestamp: `${safeCurrentTime ?? 0}`,
            total_duration: `${player.duration()}`,
            tracking_id: trackingId,
            source: source,
            variant: `${player.videoHeight()}`,
          },
        })
        if (isLogin && sessionLimit) {
          await sessionEventCall({
            data: {
              event_type: 'DELETE',
            },
          })
        }
      }

      clearInterval(timeInterval)
    })

    player.on('ended', async () => {
      if (PLAYER_STARTED) {
        handlePlayingSequence()
        const safeCurrentTime = getSafeCurrentTime()
        await analyticsEventCall({
          data: {
            event_type: 'PLAYER',
            event_name: 'PLAYER_END',
            content_id: contentInfo?.content_id,
            player_timestamp: `${safeCurrentTime ?? 0}`,
            total_duration: `${player.duration()}`,
            tracking_id: trackingId,
            source: source,
            variant: `${player.videoHeight()}`,
          },
        })
      }
      clearInterval(timeInterval)
    })

    player.on('error', () => {
      // console.log(player.error());
      let error = player.error()
      raiseToast('error', error.message)
      setError(true)
    })
  }

  console.log('player is Buffering', isBuffering, resumeTime)

  return (
    <div
      className={`flex flex-col xl:flex-row w-full h-full relative ${
        isBuffering ? 'animate-pulse' : ''
      }`}
    >
      {isBuffering && contentInfo?.asset_type === 'video' && (
        <div className="absolute inset-0 z-50 animate-pulse bg-cover bg-center pointer-events-none w-full h-full">
          <CustomImage
            imageUrl={getVideoThumbnail(accountObj?.cf_domain_name, contentInfo?.asset_id)}
            imgClass="w-full h-full"
            loaderClass="hidden"
            imgAlt={contentInfo?.asset_id}
          />
        </div>
      )}

      {isYoutubePlayer ? (
        <ExternalLinkPlayer
          youtubeData={contentInfo?.youtube_data}
          contentInfo={contentInfo}
          resumeTime={resumeTime}
          handlePlayingSequence={() => {}}
          adCampaignData={[]}
          onVideoComplete={() => {}}
          playerData={{}}
          stillWatchingAllowed={true}
          disableHeader={true}
          loop={false}
          controlBar={false}
        />
      ) : (
        <div style={{ maxWidth: '100%' }} className={`player-container w-full h-full relative`}>
          {isIptvPlayer ? (
            <IptvPlayerVideoJs
              options={videoJsOptions}
              onReady={handlePlayerReady}
              watermarkLogo={getImage(accountObj?.cf_domain_name, watermarkLogo)}
              cover={false}
              muted={!FIRST_PROGRAM_PLAYED}
              isAudioFeed={isAudioFeed}
              channelThumbnail={
                isAudioFeed
                  ? getImage(accountObj?.cf_domain_name, detailObj?.channel_thumbnail)
                  : null
              }
              pageData={pageData}
              volume={volume}
            />
          ) : (
            <ChannelPlayerVideoJs
              options={videoJsOptions}
              onReady={handlePlayerReady}
              watermarkLogo={getImage(accountObj?.cf_domain_name, watermarkLogo)}
              cover={false}
              muted={!FIRST_PROGRAM_PLAYED}
              isAudioFeed={isAudioFeed}
              channelThumbnail={
                isAudioFeed
                  ? getImage(accountObj?.cf_domain_name, detailObj?.channel_thumbnail)
                  : null
              }
            />
          )}
        </div>
      )}
    </div>
  )
}

export default ChannelPlayer
